import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { AvatarSmall } from 'components';
import { IItemCardProps } from 'models/itemcard';
import { IAuthContext } from 'models/common';
import { withAuthContext } from 'containers/AccessControl/WithAuthContext';
import { lowerFirstObjectPropertiesNames, thumbUrl } from 'helpers/common';
import { IOfficeListItem, IOfficeListItemExtended, IOfficeTime } from 'models/shop/office';
import { connect } from 'react-redux';
import { IStore } from 'redux/IStore';
import { Link } from 'react-router';
import { haversine_distance } from 'helpers/maps';
import ReviewScore from 'components/FormControls/ReviewScore';
import { formatWorkTime } from 'helpers/bookingTemplates';
import renderIcon from 'helpers/renderIcon';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

interface IProps extends IItemCardProps {
	item: IOfficeListItem | IOfficeListItemExtended;
	t?: (key: string, options?: any) => string;
	context?: IAuthContext;
	isFollowing?: boolean;
	followItem?: () => void;
	unfollowItem?: () => void;
}

interface IState {
	timezone: string;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class OfficeItemCardComponent extends React.Component<IProps & ReturnType<typeof stateToProps>, IState> {
	constructor(props) {
		super(props);

		this.onViewStoreClick = this.onViewStoreClick.bind(this);

		this.state = {
			timezone: null,
		};
	}

	private onViewStoreClick(e: React.SyntheticEvent) {
		e.preventDefault();
		if (this.props.goToCard) {
			this.props.goToCard(this.props.item.entityId);
		}
	}

	public componentDidMount() {
		const { item } = this.props;

		if (item) {
			const timezone = item?.zoneInfo;
			this.setState({ timezone });
		}
	}

	public render() {
		const { t, item, onMouseEnter, onMouseLeave, itemClicked } = this.props;

		let shopPictureToDisplay: string = '';
		if (item.coverPicture && item.coverPicture) {
			shopPictureToDisplay = item.coverPicture;
		}
		if (!shopPictureToDisplay && item.shop?.coverPicture) {
			shopPictureToDisplay = item.shop.coverPicture;
		}
		if (!shopPictureToDisplay) {
			shopPictureToDisplay = require('containers/Html/icons/cover_salon_default.svg');
		}

		const name = item.shop?.name || item.name || item.location;

		let times: IOfficeTime[];
		try {
			times = JSON.parse(item.officeTimes || '[]').map(lowerFirstObjectPropertiesNames);
		} catch (e) {
			console.error(e, item.officeTimes);
		}
		const openingTimes: string[][] = [];
		if (Array.isArray(times)) {
			for (let i = 0; i < 7; i++) {
				const time = times.find((t) => t && typeof t === 'object' && t.day === i);
				openingTimes.push([
					t('Day_' + i).substring(0, 1),
					time ? formatWorkTime(time, this.state.timezone) : t('Shop_ManageShop_Location_Label_OpeningTimes_Closed'),
				]);
			}
		}
		// console.log(openingTimes);

		let distance: JSX.Element = null;

		if ('origin' in item && item.origin && item.geoLocationLatitude && item.geoLocationLongitude) {
			const d = haversine_distance(
				item.origin.latitude,
				item.origin.longitude,
				item.geoLocationLatitude,
				item.geoLocationLongitude,
			);
			distance = <span className="muted">{d.toFixed(1)} km</span>;
		}
		const shopCode = item.shop?.code || '';

		const todayWorkTime = (dayIndex: number): string => {
			const time = openingTimes[dayIndex];
			return time[1];
		};

		return (
			<div
				className={
					'officeItemCard card dark ' +
					((item as IOfficeListItemExtended).className || '') +
					(itemClicked === item?.id ? ' gradient' : '')
				}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<div className="row no-gutters">
					<div className="col-sm-7 officeInfo">
						<div className="imageAndNameContainer d-flex flex-row">
							<div className="imageHolder">
								<Link to={`/shop/${shopCode}/${item.id}`}>
									<AvatarSmall name={name} imageUrl={item.shop?.picture} />
								</Link>
							</div>
							<div className="nameAndAddressContainer d-flex flex-column">
								<Link to={`/shop/${shopCode}/${item.id}`}>
									<h3>{name}</h3>
								</Link>
								<div className="locationContainer d-flex">
									<p className="p14">
										{item.location + ' '}
										<span></span>
									</p>
								</div>
							</div>
						</div>
						<div className="details-row">
							<div className="work-time with-border">
								{renderIcon('clock')}
								{openingTimes.length ? (
									<>
										<div className="openingTimes">
											<p className="p14">{todayWorkTime(momentWithTimezone(this.state.timezone).day())}</p>
										</div>
										<ul>
											{openingTimes.map((time, i) => (
												<li key={i}>
													<span className="muted">{time[0]}</span>
													{time[1]}
												</li>
											))}
										</ul>
									</>
								) : null}
							</div>
							<div className="distance">
								{renderIcon('pin_empty')}
								{distance}
							</div>
							<div className="rating">
								<ReviewScore averageRating={item.shopAverageRating} votes={item.votes} />
							</div>
						</div>
					</div>
					<figure
						className="col-sm-5 shopPicture"
						style={{ backgroundImage: 'url(' + thumbUrl(shopPictureToDisplay, 'thumbnail') + ')' }}
					>
						<div className="imageHolder">
							<Link to={`/shop/${shopCode}/${item.id}`}>
								<AvatarSmall name={name} imageUrl={item.shop?.picture} fromShop={true} />
							</Link>
						</div>
					</figure>
				</div>
			</div>
		);
	}
}

const stateToProps = (state: IStore) => ({
	countryCallingCodes: state.domainValues.countryCallingCodes,
	fetchDefault: state.fetchDefault,
});

const OfficeItemCard = connect(stateToProps)(withAuthContext(OfficeItemCardComponent));
export { OfficeItemCard };
